'use client';

import { useState } from 'react';
import { Button } from '@heroui/react';
import { FaTag, FaCircleExclamation, FaXmark } from 'react-icons/fa6';
import { useScheduler } from '../SchedulerContext';
import { formatDiscountLabel } from '@/util/functions/formatDiscountLabel';
import { discountPendingMessage } from '../utils/schedulerPrefill';

/**
 * Explains what a scheduling link pre-filled.
 *
 * Two jobs:
 * 1. Confirm a link-applied discount from step 1 onward. The summary bar only
 *    shows a total once a property is entered, and the discount input lives on
 *    the final step — so without this the customer walks most of the funnel
 *    with no sign the advertised discount landed.
 * 2. Say plainly when part of the link could not be honored (stale service,
 *    expired promo) or when a cart change dropped an applied discount, rather
 *    than letting the price move unexplained.
 * 3. Name the service that would switch on a code the cart does not qualify for
 *    yet. A campaign can advertise a discount on something the customer has not
 *    added — "does not apply" alone leaves them stuck at full price with no way
 *    to tell what to do about it.
 */
export default function SchedulerPrefillBanner() {
  const {
    prefill,
    prefillNotices,
    dismissPrefillNotices,
    appliedDiscountCode,
    pendingDiscountCode,
    pendingDiscountUnlockServices,
    companyStyles,
  } = useScheduler();

  // Only announce a discount the *link* carried — one the customer typed in on
  // the summary step already has its own confirmation chip there. A code the
  // link parked as pending counts: when adding a service finally switches it on,
  // that is exactly the moment worth confirming.
  const linkDiscountId =
    prefill.discountCode?._id ?? prefill.pendingDiscountCode?._id ?? null;
  const linkDiscount =
    linkDiscountId && appliedDiscountCode?._id === linkDiscountId
      ? appliedDiscountCode
      : null;

  // Only offer the call to action when there is something to act on. A code
  // covering nothing this scheduler offers has no next step to name, so it falls
  // through to the ordinary notices.
  const pendingDiscount =
    pendingDiscountCode && pendingDiscountUnlockServices.length > 0
      ? pendingDiscountCode
      : null;

  // Remember *what* was dismissed rather than a plain flag: a later notice
  // (e.g. a discount dropped when the cart changed) is new information, so it
  // re-opens a banner the customer already dismissed.
  const [dismissedSignature, setDismissedSignature] = useState<string | null>(null);
  const pendingSignature = pendingDiscount ? `pending:${pendingDiscount._id}` : '';
  const noticeSignature = prefillNotices.map((notice) => notice.code).join('|');
  const signature = `${pendingSignature}#${noticeSignature}`;
  const isDismissed = dismissedSignature === signature;

  if (
    isDismissed ||
    (!linkDiscount && !pendingDiscount && prefillNotices.length === 0)
  ) {
    return null;
  }

  const handleDismiss = () => {
    // Notices are cleared, so the signature that survives is the pending half —
    // which keeps the banner closed until something new arrives. A still-pending
    // code is state rather than news, so it must not re-open it.
    setDismissedSignature(`${pendingSignature}#`);
    dismissPrefillNotices();
  };

  const hasNotices = prefillNotices.length > 0;
  // A code waiting on a service is an action the customer still has to take, so
  // it wears the same "needs attention" treatment as an unhonored link.
  const needsAttention = hasNotices || Boolean(pendingDiscount);

  return (
    <div
      data-testid='scheduler-prefill-banner'
      className={`relative border-b ${
        needsAttention
          ? 'border-warning-300 bg-warning-50'
          : 'border-success-300 bg-success-50'
      }`}
    >
      <div className='mx-auto max-w-7xl px-3 py-2.5 sm:px-6 sm:py-3 lg:px-8'>
        <div className='flex items-start gap-2.5 sm:items-center sm:gap-3'>
          <div
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full sm:h-10 sm:w-10 ${
              needsAttention ? 'bg-warning-200' : 'bg-success-200'
            }`}
          >
            {needsAttention ? (
              <FaCircleExclamation className='text-warning-800' size={16} />
            ) : (
              <FaTag className='text-success-800' size={16} />
            )}
          </div>

          <div className='min-w-0 flex-1'>
            {linkDiscount && (
              <p
                className={`text-sm font-medium sm:text-base ${
                  needsAttention ? 'text-warning-900' : 'text-success-900'
                }`}
              >
                Discount code{' '}
                <span
                  className='font-semibold'
                  style={{ color: companyStyles.companyColor || undefined }}
                >
                  {linkDiscount.code}
                </span>{' '}
                applied — {formatDiscountLabel(linkDiscount)}
              </p>
            )}

            {pendingDiscount && (
              <p
                data-testid='scheduler-prefill-discount-pending'
                className='text-sm font-medium text-warning-900 sm:text-base'
              >
                {discountPendingMessage(
                  pendingDiscount.code,
                  pendingDiscountUnlockServices
                )}
              </p>
            )}

            {hasNotices && (
              <ul
                className={`space-y-0.5 text-xs text-warning-700 sm:text-sm ${
                  linkDiscount || pendingDiscount ? 'mt-0.5' : ''
                }`}
              >
                {prefillNotices.map((notice) => (
                  <li key={`${notice.code}-${notice.message}`}>
                    {notice.message}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Button
            size='sm'
            variant='light'
            color='default'
            onPress={handleDismiss}
            isIconOnly
            className='shrink-0'
            aria-label='Dismiss'
          >
            <FaXmark size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
